import type { SignUpRequest } from '@/types';

import React from 'react';

import { Box, Button, PinInput, Stack, VStack } from '@/components';
import {
  useSetUpOtp,
  useSignUpOtp,
  useSignUpVerifyOtp,
  useTimer,
  useTranslation,
} from '@/hooks';
import { isValidOtp } from '@/utils';

type SubmitCodeFormProps = {
  goNext: (data: { verified_sms: boolean; token?: string }) => void;
  mobile: string;
  country_code_prefix: SignUpRequest['country_phone_prefix'];
  canSkip: boolean;
  otpTime: number;
};

const SubmitCodeForm: React.FC<SubmitCodeFormProps> = ({
  goNext,
  mobile,
  country_code_prefix,
  canSkip,
  otpTime,
}) => {
  const { t } = useTranslation('pages.signUp.form.submitCodeForm');
  const formRef = React.useRef<HTMLFormElement>(null);

  const { counter, setCounter } = useTimer(otpTime || 10);

  const { mutateAsync: verifyCode, isPending: verifyLoading } =
    useSignUpVerifyOtp();
  const { mutateAsync: getOtp, isPending: getOtpLoading } = useSignUpOtp();
  const [value, setValue] = React.useState('');

  const onSubmit = (
    valuePinInput: string,
    number: number
  ): Promise<void> | null => {
    if (isValidOtp({ value: valuePinInput, number })) {
      return verifyCode({
        code: parseInt(valuePinInput),
        mobile,
        country_code_prefix,
      }).then(({ token }) => goNext({ verified_sms: true, token }));
    }
    return null;
  };

  const onResend = () => {
    setValue('');
    void getOtp({ mobile, country_code_prefix })
      .then(() => {
        setCounter(120);
      })
      .catch();
  };

  const MemoizedOnOtpReceived = React.useCallback((otpValue: string) => {
    setValue(otpValue);
    void onSubmit(otpValue, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSetUpOtp(MemoizedOnOtpReceived);
  return (
    <Stack
      flex={1}
      width="100%"
      h="100%"
      align="flex-start"
      justify="space-between"
      gap={4}
      as={'form'}
      ref={formRef}
    >
      <VStack justifyContent={'start'} width="100%" flexGrow={1} mt={6}>
        <PinInput
          numberOfPinInputField={6}
          setValuePinInput={setValue}
          onComplete={onSubmit}
          value={value}
        />
        {!canSkip && (
          <Button
            width="192px"
            variant="link"
            isDisabled={counter !== 0}
            onClick={onResend}
            mt={6}
            justifyContent="center"
            isLoading={getOtpLoading}
          >
            {`${t('resendCode')} : 0${Math.floor(counter / 60)}:${counter % 60 < 10 ? '0' : ''}${counter % 60}`}
          </Button>
        )}
      </VStack>
      <Box
        display="flex"
        w="100%"
        alignItems="center"
        justifyContent={canSkip ? 'flex-end' : 'space-between'}
        height={'100%'}
      >
        <Button
          w="full"
          type="submit"
          onClick={() => void onSubmit(value, 6)}
          isLoading={verifyLoading}
        >
          {t('submitCode')}
        </Button>
      </Box>
      {/* {canSkip && (
        <Button
          mt="auto"
          w="full"
          textAlign="center"
          variant="link"
          textDecor="underline"
          onClick={() => goNext({ verified_sms: false })}
        >
          {t('skip')}
        </Button>
      )} */}
    </Stack>
  );
};
export default SubmitCodeForm;
